import { UserIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";

import { PromptTemplateBlockCore } from "../PromptTemplateBlock";

import { ConversationSamplesDialog } from "./ConversationSamplesDialog";

import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import BlockField from "@/components/PowerBlock/Block/BlockField";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { custom } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Toggle } from "@/components/Toggle";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ConversationSimulatorConfiguration, PromptTemplateConfiguration } from "@/types/evaluate";

const validationSchema = createBlockSchema<ConversationSimulatorConfiguration>()
  .requireName()
  .addRule({
    path: "configuration.template",
    validate: (_value, fullPayload) => {
      // Valid if we have either a registry template with a name OR an inline template
      const hasRegistryTemplate = !!fullPayload.configuration?.template?.name;
      const hasInlineTemplate = !!fullPayload.configuration?.inline_template?.prompt_template;
      return hasRegistryTemplate || hasInlineTemplate;
    },
    message: "Template selection is required",
  })
  .addRule(
    custom(
      "configuration",
      (_, payload) => {
        const config = payload.configuration;
        const hasUserPersona = config?.user_persona && config.user_persona.trim() !== "";
        const hasUserPersonaSource =
          config?.user_persona_source && config.user_persona_source.trim() !== "";
        return Boolean(hasUserPersona || hasUserPersonaSource);
      },
      "Please provide either a user persona description or select a source variable",
      ["configuration.user_persona", "configuration.user_persona_source"],
    ),
  )
  .build();

export const ConversationSimulatorBlockCore = observer(
  (props: Omit<BlockProps<ConversationSimulatorConfiguration>, "DefaultFields">) => {
    const blockStore = useBlock<ConversationSimulatorConfiguration>();
    const isReadonly = useIsReadonly();
    const [personaSourceEnabled, setPersonaSourceEnabled] = useState<boolean>(
      !!blockStore.configuration?.user_persona_source,
    );
    const [completionGuidanceEnabled, setCompletionGuidanceEnabled] = useState<boolean>(
      !!blockStore.configuration?.conversation_completed_prompt ||
        !!blockStore.configuration?.conversation_completed_prompt_source,
    );
    const [completedPromptSourceEnabled, setCompletedPromptSourceEnabled] = useState<boolean>(
      !!blockStore.configuration?.conversation_completed_prompt_source,
    );
    const [showSamplesDialog, setShowSamplesDialog] = useState<boolean>(false);

    const handlePersonaChange = useCallback(
      (value: string) => {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          user_persona: value,
        } as ConversationSimulatorConfiguration);
      },
      [blockStore],
    );

    const handleCompletedPromptChange = useCallback(
      (value: string) => {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          conversation_completed_prompt: value,
        } as ConversationSimulatorConfiguration);
      },
      [blockStore],
    );

    const handleUserFirstChange = useCallback(
      (enabled: boolean) => {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          is_user_first: enabled,
        } as ConversationSimulatorConfiguration);
      },
      [blockStore],
    );

    const handleConversationSamplesChange = useCallback(
      (samples: string[]) => {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          conversation_samples: samples,
        } as ConversationSimulatorConfiguration);
      },
      [blockStore],
    );

    const handleMaxTurnsChange = useCallback(
      (value: string) => {
        const numValue = value === "" ? undefined : parseInt(value, 10);
        if (numValue !== undefined && (isNaN(numValue) || numValue < 1 || numValue > 150)) {
          return;
        }
        blockStore.setConfiguration({
          ...blockStore.configuration,
          max_turns: numValue,
        } as ConversationSimulatorConfiguration);
      },
      [blockStore],
    );

    return (
      <>
        <PromptTemplateBlockCore
          {...(props as BlockProps<PromptTemplateConfiguration>)}
          showVerboseMode={false}
        />

        <SectionGroup
          title="Conversation Configuration"
          description="Configure the user persona and conversation settings"
          icon={<UserIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="User Persona" path="configuration">
              {personaSourceEnabled ? (
                <SingleSourceSelector
                  sources={props.sources!}
                  sourceKey="user_persona_source"
                  placeholder="Select a source..."
                />
              ) : (
                <Textarea
                  value={blockStore.configuration?.user_persona || ""}
                  onChange={e => handlePersonaChange(e.target.value)}
                  placeholder="Describe the persona of the user in the conversation..."
                  rows={3}
                  disabled={isReadonly}
                />
              )}
            </ValidatedField>

            <BlockField name="Use Source Variable for User Persona">
              <Checkbox
                id="use-persona-source"
                checked={personaSourceEnabled}
                disabled={isReadonly}
                onChange={e => {
                  setPersonaSourceEnabled(e.target.checked);
                  if (e.target.checked) {
                    blockStore.setConfiguration({
                      ...blockStore.configuration,
                      user_persona: undefined,
                      user_persona_source: "",
                    } as ConversationSimulatorConfiguration);
                  } else {
                    blockStore.setConfiguration({
                      ...blockStore.configuration,
                      user_persona_source: undefined,
                      user_persona: "",
                    } as ConversationSimulatorConfiguration);
                  }
                }}
                label="Use source variable for user persona"
              />
            </BlockField>

            <BlockField name="User Goes First">
              <Toggle
                enabled={blockStore.configuration?.is_user_first ?? false}
                setEnabled={handleUserFirstChange}
                disabled={isReadonly}
              />
            </BlockField>

            <BlockField name="Max Turns">
              <Input
                type="number"
                min={1}
                max={150}
                value={blockStore.configuration?.max_turns ?? ""}
                onChange={e => handleMaxTurnsChange(e.target.value)}
                placeholder="20 (default)"
                disabled={isReadonly}
              />
            </BlockField>

            <BlockField name="Conversation Samples">
              <Button
                variant="outline"
                onClick={() => setShowSamplesDialog(true)}
                className="w-full justify-between"
                disabled={isReadonly}
              >
                <div className="flex items-center gap-2">
                  <span>
                    {(blockStore.configuration?.conversation_samples?.length || 0) === 0
                      ? "No samples"
                      : `${blockStore.configuration?.conversation_samples?.length} sample${
                          blockStore.configuration?.conversation_samples?.length !== 1 ? "s" : ""
                        }`}
                  </span>
                </div>
                <span className="text-xs text-gray-500">Manage</span>
              </Button>
            </BlockField>

            <BlockField name="Custom Completion Condition">
              <Checkbox
                id="enable-completion-guidance"
                checked={completionGuidanceEnabled}
                disabled={isReadonly}
                onChange={e => {
                  setCompletionGuidanceEnabled(e.target.checked);
                  if (!e.target.checked) {
                    // Clear both fields when disabling
                    blockStore.setConfiguration({
                      ...blockStore.configuration,
                      conversation_completed_prompt: undefined,
                      conversation_completed_prompt_source: undefined,
                    } as ConversationSimulatorConfiguration);
                    setCompletedPromptSourceEnabled(false);
                  }
                }}
                label="Define when conversation should end"
              />
            </BlockField>

            {completionGuidanceEnabled && (
              <>
                <BlockField name="Completion Guidance">
                  {completedPromptSourceEnabled ? (
                    <SingleSourceSelector
                      sources={props.sources!}
                      sourceKey="conversation_completed_prompt_source"
                      placeholder="Select a source..."
                    />
                  ) : (
                    <Textarea
                      value={blockStore.configuration?.conversation_completed_prompt || ""}
                      onChange={e => handleCompletedPromptChange(e.target.value)}
                      placeholder="e.g., 'End when the user confirms their order' or 'Complete when the assistant calls the submit_order tool'"
                      rows={2}
                      disabled={isReadonly}
                    />
                  )}
                </BlockField>

                <BlockField name="Use Source Variable for Completion Guidance">
                  <Checkbox
                    id="use-completed-prompt-source"
                    checked={completedPromptSourceEnabled}
                    disabled={isReadonly}
                    onChange={e => {
                      setCompletedPromptSourceEnabled(e.target.checked);
                      if (e.target.checked) {
                        blockStore.setConfiguration({
                          ...blockStore.configuration,
                          conversation_completed_prompt: undefined,
                          conversation_completed_prompt_source: "",
                        } as ConversationSimulatorConfiguration);
                      } else {
                        blockStore.setConfiguration({
                          ...blockStore.configuration,
                          conversation_completed_prompt_source: undefined,
                          conversation_completed_prompt: "",
                        } as ConversationSimulatorConfiguration);
                      }
                    }}
                    label="Use source variable"
                  />
                </BlockField>
              </>
            )}
          </div>
        </SectionGroup>

        <ConversationSamplesDialog
          open={showSamplesDialog}
          onOpenChange={setShowSamplesDialog}
          samples={blockStore.configuration?.conversation_samples || []}
          onSamplesChange={handleConversationSamplesChange}
        />
      </>
    );
  },
);

export default withBlockStore<ConversationSimulatorConfiguration>(ConversationSimulatorBlockCore, {
  validationSchema,
});
